import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';
import './AdminAccount.css';

const CategoriesPage = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [workzonesByLoc, setWorkzonesByLoc] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, []);

  const loadData = async (uid) => {
    try {
      setLoading(true);
      const db = getFirestore();
      
      const categoriesSnapshot = await getDocs(
        query(collection(db, 'users', uid, 'categories'), orderBy('order'))
      );
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
      
      const locationsSnapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      const locationsData = locationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLocations(locationsData);

      // Load workzones for all locations
      const wzData = {};
      for (const loc of locationsData) {
        const wzSnapshot = await getDocs(collection(db, 'users', uid, 'locations', loc.id, 'workzones'));
        wzData[loc.id] = wzSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      setWorkzonesByLoc(wzData);
    } catch (error) {
      console.error('Error loading categories:', error);
      Swal.fire('Error', 'Failed to load categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    const result = await Swal.fire({
      title: 'Delete Category?',
      text: `Are you sure you want to delete "${category.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });

    if (result.isConfirmed && user) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'categories', category.id));
        await loadData(user.uid);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'Category deleted', 'success');
      } catch (error) {
        console.error('Error deleting category:', error);
        Swal.fire('Error', 'Failed to delete category', 'error');
      }
    }
  };

  const handleReorder = async (newCategories) => {
    setCategories(newCategories);
    
    try {
      const db = getFirestore();
      for (let i = 0; i < newCategories.length; i++) {
        await updateDoc(doc(db, 'users', user.uid, 'categories', newCategories[i].id), {
          order: i
        });
      }
    } catch (error) {
      console.error('Error reordering categories:', error);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedItem !== null && draggedItem !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (dragIndex !== dropIndex && !isNaN(dragIndex)) {
      const newCategories = [...categories];
      const [movedItem] = newCategories.splice(dragIndex, 1);
      newCategories.splice(dropIndex, 0, movedItem);
      handleReorder(newCategories);
    }
    
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleSaveCategory = async (formValues) => {
    if (!user) return;
    
    try {
      const db = getFirestore();
      if (editingCategory) {
        await updateDoc(doc(db, 'users', user.uid, 'categories', editingCategory.id), {
          name: formValues.name,
          description: formValues.description,
          imageUrl: formValues.imageUrl || null,
          availableLocations: formValues.availableLocations,
          workzonesByLocation: formValues.workzonesByLocation,
          allDayAvailable: formValues.allDayAvailable,
          fromTime: formValues.fromTime,
          toTime: formValues.toTime,
          order: formValues.order,
        });
      } else {
        await setDoc(doc(collection(db, 'users', user.uid, 'categories')), {
          name: formValues.name,
          description: formValues.description,
          imageUrl: formValues.imageUrl || null,
          availableLocations: formValues.availableLocations,
          workzonesByLocation: formValues.workzonesByLocation,
          allDayAvailable: formValues.allDayAvailable,
          fromTime: formValues.fromTime,
          toTime: formValues.toTime,
          order: formValues.order,
          createdAt: serverTimestamp(),
        });
      }
      
      setShowModal(false);
      setEditingCategory(null);
      await loadData(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', `Category ${editingCategory ? 'updated' : 'created'}`, 'success');
    } catch (error) {
      console.error('Error saving category:', error);
      Swal.fire('Error', 'Failed to save category', 'error');
    }
  };

  const openAddModal = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  if (loading) {
    return (
      <AdminLayout title="Categories">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Categories">
      <SetupGuard prerequisites={['location']}>
      <div className="admin-account-page">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="m-0">Manage Categories</h5>
              <button className="btn btn-primary" onClick={openAddModal}>
                <i className="fas fa-plus mr-2"></i>Add Category
              </button>
            </div>
          </div>
        </div>

        {/* Category List */}
        <div className="row">
          {categories.length === 0 ? (
            <div className="col-12 text-center py-5">
              <i className="fas fa-tags fa-4x text-muted mb-3"></i>
              <h5 className="text-muted">No categories yet</h5>
              <p className="text-muted">Click "Add Category" to create your first category</p>
            </div>
          ) : (
            <div className="col-12">
              <p className="text-muted mb-2">
                <small><i className="fas fa-info-circle mr-1"></i>Drag items to reorder</small>
              </p>
              {categories.map((category, index) => (
                <div 
                  key={category.id} 
                  className="card mb-3"
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  style={{
                    cursor: 'grab',
                    borderLeft: dragOverIndex === index ? '4px solid #ff6b35' : '4px solid transparent',
                    opacity: draggedItem === index ? 0.5 : 1,
                    transform: dragOverIndex === index ? 'translateX(10px)' : 'none',
                    transition: 'all 0.2s ease',
                    background: dragOverIndex === index ? 'rgba(255,107,53,0.05)' : '#fff',
                  }}
                >
                  <div className="card-body d-flex align-items-center">
                    <div className="mr-3" style={{ color: '#ccc', cursor: 'grab' }}>
                      <i className="fas fa-grip-vertical fa-lg"></i>
                    </div>
                    {category.imageUrl ? (
                      <img 
                        src={category.imageUrl} 
                        alt={category.name}
                        className="mr-3"
                        style={{ width: 50, height: 50, borderRadius: 8, objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center mr-3"
                        style={{
                          width: 50,
                          height: 50,
                          borderRadius: 8,
                          background: 'rgba(255,107,53,0.1)',
                          color: '#ff6b35',
                        }}
                      >
                        <i className="fas fa-tags"></i>
                      </div>
                    )}
                    <div className="flex-grow-1">
                      <h6 className="m-0 font-weight-bold">{category.name}</h6>
                      <small className="text-muted">
                        {category.description}
                        {category.allDayAvailable ? ' \u2022 All Day' : ` \u2022 ${category.fromTime || 'N/A'} - ${category.toTime || 'N/A'}`}
                      </small>
                    </div>
                    <div className="d-flex align-items-center">
                      <button 
                        className="btn btn-sm btn-link text-primary"
                        onClick={() => openEditModal(category)}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        className="btn btn-sm btn-link text-danger"
                        onClick={() => handleDeleteCategory(category)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category Modal */}
        {showModal && (
          <CategoryFormModal
            category={editingCategory}
            locations={locations}
            workzonesByLoc={workzonesByLoc}
            categories={categories}
            onSave={handleSaveCategory}
            onClose={() => { setShowModal(false); setEditingCategory(null); }}
            user={user}
          />
        )}
      </div>
      </SetupGuard>
    </AdminLayout>
  );
};

const CategoryFormModal = ({ category, locations, workzonesByLoc, categories, onSave, onClose, user }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    imageUrl: category?.imageUrl || '',
    availableLocations: category?.availableLocations || [],
    workzonesByLocation: category?.workzonesByLocation || {},
    allDayAvailable: category?.allDayAvailable || false,
    fromTime: category?.fromTime || '09:00',
    toTime: category?.toTime || '17:00',
    order: category?.order ?? categories.length,
  });
  const [errors, setErrors] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const toggleLocation = (locId) => {
    setFormData(prev => {
      const newLocs = prev.availableLocations.includes(locId)
        ? prev.availableLocations.filter(id => id !== locId)
        : [...prev.availableLocations, locId];
      
      // Remove workzone for unselected location
      const newWzs = { ...prev.workzonesByLocation };
      if (!newLocs.includes(locId)) {
        delete newWzs[locId];
      }
      
      return { ...prev, availableLocations: newLocs, workzonesByLocation: newWzs };
    });
  };

  const setWorkzone = (locId, wzId) => {
    setFormData(prev => ({
      ...prev,
      workzonesByLocation: { ...prev.workzonesByLocation, [locId]: wzId }
    }));
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploadingImage(true);
    try {
      const storage = getStorage();
      const userName = user.displayName?.toLowerCase().replace(/\s+/g, '-') || 'user';
      const folderPath = `${userName}-${user.uid}/categories`;
      const storageRef = ref(storage, `${folderPath}/${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, imageUrl: downloadUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      Swal.fire('Error', 'Failed to upload image', 'error');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Please enter a category name';
    if (!formData.description.trim()) errs.description = 'Please enter a description';
    if (formData.availableLocations.length === 0) errs.locations = 'Select at least one location';
    
    const missingWz = formData.availableLocations.some(locId => !formData.workzonesByLocation[locId]);
    if (missingWz) errs.workzones = 'Select a workzone for each location';
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">{category ? 'Edit Category' : 'Add Category'}</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Category Name *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      className="form-control"
                      style={{ width: 100 }}
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                  rows="2"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
                {errors.description && <div className="invalid-feedback">{errors.description}</div>}
              </div>

              <div className="form-group">
                <label>Category Image</label>
                <input
                  type="file"
                  accept="image/*"
                  className="d-none"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                />
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    height: 120,
                    borderRadius: 12,
                    border: '2px dashed #e0e0e0',
                    background: '#f8f9fa',
                    cursor: 'pointer',
                    overflow: 'hidden',
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingImage ? (
                    <div className="text-center">
                      <img src="/fodyfee_loading.png" alt="Uploading..." style={{ width: 40, height: 40, objectFit: 'contain' }} />
                      <div className="mt-2 text-muted">Uploading...</div>
                    </div>
                  ) : formData.imageUrl ? (
                    <img
                      src={formData.imageUrl}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="text-center">
                      <i className="fas fa-camera fa-2x text-muted"></i>
                      <div className="mt-2 text-muted">Tap to add photo</div>
                    </div>
                  )}
                </div>
                {formData.imageUrl && (
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger"
                      onClick={() => setFormData({ ...formData, imageUrl: '' })}
                    >
                      <i className="fas fa-trash mr-1"></i>Remove Image
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="d-block">Available Locations *</label>
                {errors.locations && <small className="text-danger d-block mb-2">{errors.locations}</small>}
                <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                  {locations.map(loc => {
                    const isSelected = formData.availableLocations.includes(loc.id);
                    return (
                      <button
                        key={loc.id}
                        type="button"
                        className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{
                          borderRadius: '16px',
                          backgroundColor: isSelected ? '#ff6b35' : undefined,
                          borderColor: isSelected ? '#ff6b35' : undefined,
                        }}
                        onClick={() => toggleLocation(loc.id)}
                      >
                        <i className={`fas ${isSelected ? 'fa-check' : 'fa-plus'} mr-1`}></i>
                        {loc.companyName || loc.locationName}
                      </button>
                    );
                  })}
                </div>
              </div>

              {formData.availableLocations.length > 0 && (
                <div className="form-group">
                  <label className="d-block">Workzones by Location *</label>
                  {errors.workzones && <small className="text-danger d-block mb-2">{errors.workzones}</small>}
                  <div className="row">
                    {formData.availableLocations.map(locId => {
                      const loc = locations.find(l => l.id === locId);
                      const wzs = workzonesByLoc[locId] || [];
                      return (
                        <div key={locId} className="col-md-6 mb-3">
                          <small className="text-muted font-weight-bold d-block mb-1">{loc?.companyName || loc?.locationName}</small>
                          {wzs.length === 0 ? (
                            <small className="text-danger"><i className="fas fa-exclamation-circle mr-1"></i>No workzones available</small>
                          ) : (
                            <select
                              className="form-control"
                              value={formData.workzonesByLocation[locId] || ''}
                              onChange={(e) => setWorkzone(locId, e.target.value)}
                            >
                              <option value="">Select workzone</option>
                              {wzs.map(wz => (
                                <option key={wz.id} value={wz.id}>{wz.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="form-group">
                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="allDayAvailable"
                    checked={formData.allDayAvailable}
                    onChange={(e) => setFormData({ ...formData, allDayAvailable: e.target.checked })}
                  />
                  <label className="custom-control-label" htmlFor="allDayAvailable">All Day Available</label>
                </div>
              </div>

              {!formData.allDayAvailable && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>From</label>
                      <input
                        type="time"
                        className="form-control"
                        value={formData.fromTime}
                        onChange={(e) => setFormData({ ...formData, fromTime: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>To</label>
                      <input
                        type="time"
                        className="form-control"
                        value={formData.toTime}
                        onChange={(e) => setFormData({ ...formData, toTime: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}>
                {category ? 'Update' : 'Create'} Category
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
